import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import {settings} from '@/plugins/settings';
import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import responseHandler from '@/plugins/errorHandling';

export interface ITicketEmail {
  id: number;
  from_email: string;
  to_email: string;
  subject: string;
  body: string;
  snippet: string | null;
  status: string;
  received_at: string;
  thread_id: string | null;
}

export interface ITicketSentEmail {
  id: number;
  to_email: string;
  subject: string;
  body: string;
  sent_by: string | null;
  sent_at: string;
  ticket_id: number | null;
  in_reply_to_email_id: number | null;
  cc_emails?: string[] | null;
  bcc_emails?: string[] | null;
}

export interface ILinkedUser {
  id: number;
  ticket_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  created_at?: string;
}

export interface IHelpdeskTicket {
  id: number;
  ticket_type_id: number | null;
  subject: string;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  reporter_email: string | null;
  assignee_email: string | null;
  description: string | null;
  assigned_user_id: number | null;
  ticket_type?: {id: number; name: string};
  messages_count?: number;
  emails?: ITicketEmail[];
  sent_emails?: ITicketSentEmail[];
  linked_users?: ILinkedUser[];
  // Received emails of the ticket still pending (unread): a customer's reply in the thread.
  unread_emails_count?: number;
  last_email_received_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface TicketsState {
  tickets: IHelpdeskTicket[];
  currentTicket: IHelpdeskTicket | null;
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  errors: any;
}

const state: TicketsState = {
  tickets:       [],
  currentTicket: null,
  currentPage:   1,
  lastPage:      1,
  total:         0,
  perPage:       15,
  errors:        {},
};

const getters: GetterTree<TicketsState, RootState> = {
  tickets:       (state) => state.tickets,
  currentTicket: (state) => state.currentTicket,
  currentPage:   (state) => state.currentPage,
  lastPage:      (state) => state.lastPage,
  total:         (state) => state.total,
  perPage:       (state) => state.perPage,
};

const mutations: MutationTree<TicketsState> = {
  errors(state, payload: any) { state.errors = payload; },
  ticketsLoaded(state, payload: any) {
    state.tickets     = payload.data;
    state.currentPage = payload.current_page;
    state.lastPage    = payload.last_page;
    state.total       = payload.total;
    state.perPage     = payload.per_page;
  },
  ticketLoaded(state, payload: IHelpdeskTicket) {
    state.currentTicket = payload;
  },
  ticketCreated(_state, _payload: any) {},
  // The PATCH response omits counts and relations: merged so the ones already loaded are kept.
  ticketUpdated(state, payload: IHelpdeskTicket) {
    const idx = state.tickets.findIndex(t => t.id === payload.id);
    if (idx !== -1) state.tickets.splice(idx, 1, {...state.tickets[idx], ...payload});
    if (state.currentTicket && state.currentTicket.id === payload.id) {
      state.currentTicket = {...state.currentTicket, ...payload};
    }
  },
  // Only the counter: the emails keep their status so the open ticket still shows which were new.
  ticketEmailsRead(state, payload: {ticket_id: number; updated: number}) {
    const ticket = state.tickets.find(t => t.id === payload.ticket_id);
    if (ticket) ticket.unread_emails_count = 0;
    if (state.currentTicket && state.currentTicket.id === payload.ticket_id) {
      state.currentTicket = {...state.currentTicket, unread_emails_count: 0};
    }
  },
  ticketDeleted(_state, _payload: any) {},
  templateReplySent(_state, _payload: any) {},
};

const BASE = () => settings.helpdeskEndpoint + 'v1/tickets';

const actions: ActionTree<TicketsState, RootState> = {
  fetchTickets: ({commit}, params: {page?: number; per_page?: number; statuses?: string[]; email_id?: number; search?: string; ticket_type_id?: number; has_unread?: boolean; sort?: string} | undefined) => {
    const query = new URLSearchParams();
    if (params && params.page)           query.set('page',           String(params.page));
    if (params && params.per_page)       query.set('per_page',       String(params.per_page));
    if (params && params.email_id)       query.set('email_id',       String(params.email_id));
    if (params && params.search)         query.set('search',         params.search);
    if (params && params.ticket_type_id) query.set('ticket_type_id', String(params.ticket_type_id));
    if (params && params.has_unread)     query.set('has_unread',     '1');
    if (params && params.sort)           query.set('sort',           params.sort);
    if (params && params.statuses && params.statuses.length) {
      params.statuses.forEach(s => query.append('statuses[]', s));
    }
    const qs = query.toString() ? '?' + query.toString() : '';
    return responseHandler.handle(
      () => helpdeskAxios({url: BASE() + qs, method: 'GET'}),
      commit, 'ticketsLoaded',
    );
  },

  fetchTicket: ({commit}, id: number) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + '/' + id, method: 'GET'}),
    commit, 'ticketLoaded',
  ),

  // The response only says how many: the ticket id is added so the mutation knows which one.
  markTicketEmailsAsRead: ({commit}, id: number) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + '/' + id + '/emails/read', method: 'POST'}).then((response: any) => {
      response.data.data = {...response.data.data, ticket_id: id};
      return response;
    }),
    commit, 'ticketEmailsRead',
  ),

  createTicket: ({commit}, data: Partial<IHelpdeskTicket> & {email_id?: number}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE(), method: 'POST', data}),
    commit, 'ticketCreated', null, false,
  ),

  updateTicket: ({commit}, {id, data}: {id: number; data: Partial<IHelpdeskTicket>}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + '/' + id, method: 'PATCH', data}),
    commit, 'ticketUpdated',
  ),

  deleteTicket: ({commit}, id: number) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + '/' + id, method: 'DELETE'}),
    commit, 'ticketDeleted', null, false,
  ),

  sendTemplateReply: ({commit}, {ticketId, templateId, accountId, signatureBody, sentBy}: {ticketId: number; templateId: number; accountId?: number; signatureBody?: string; sentBy?: string}) => {
    const data: any = {template_id: templateId};
    if (accountId)     data.account_id     = accountId;
    if (signatureBody) data.signature_body = signatureBody;
    if (sentBy)        data.sent_by        = sentBy;
    return responseHandler.handle(
      () => helpdeskAxios({url: BASE() + '/' + ticketId + '/send-template', method: 'POST', data}),
      commit, 'templateReplySent', null, false,
    );
  },
};

export const helpdeskTicketsModule: Module<TicketsState, RootState> = {
  namespaced: true, state, getters, mutations, actions,
};
